import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Calendar as CalendarIcon, MapPin, Users, Plus } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Events({ user, logout }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [location, setLocation] = useState("");
  const [maxAttendees, setMaxAttendees] = useState("");

  const isAdmin = ["convenor", "captain"].includes(user.role);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  const createEvent = async () => {
    if (!title || !description || !date || !location) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      await axios.post(`${API}/events`, {
        title,
        description,
        date: new Date(date).toISOString(),
        location,
        max_attendees: maxAttendees ? parseInt(maxAttendees) : null
      }, { withCredentials: true });
      
      toast.success("Event created successfully!");
      setShowCreateDialog(false);
      setTitle("");
      setDescription("");
      setDate("");
      setLocation("");
      setMaxAttendees("");
      fetchEvents();
    } catch (error) {
      console.error("Error creating event:", error);
      toast.error("Failed to create event");
    }
  };

  const signupForEvent = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/signup`, {}, {
        withCredentials: true
      });
      toast.success("Signed up successfully!");
      fetchEvents();
    } catch (error) {
      console.error("Error signing up:", error);
      toast.error(error.response?.data?.detail || "Failed to sign up");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" className="text-slate-400 hover:text-white hover:bg-white/5" data-testid="back-button">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl md:text-2xl font-bold text-yellow-500 tracking-wide font-display">SCC Football</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-display" data-testid="page-title">Events Calendar</h2>
          {isAdmin && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold" data-testid="create-event-button">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Event
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="create-event-dialog">
                <DialogHeader>
                  <DialogTitle>Create New Event</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Event Title</label>
                    <Input
                      placeholder="Event name"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      data-testid="event-title-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Description</label>
                    <Textarea
                      placeholder="Event details..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      data-testid="event-description-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Date & Time</label>
                    <Input
                      type="datetime-local"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      data-testid="event-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Location</label>
                    <Input
                      placeholder="Venue"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      data-testid="event-location-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Max Attendees (optional)</label>
                    <Input
                      type="number"
                      placeholder="Leave empty for unlimited"
                      value={maxAttendees}
                      onChange={(e) => setMaxAttendees(e.target.value)}
                      data-testid="event-max-attendees-input"
                    />
                  </div>
                  <Button
                    onClick={createEvent}
                    className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
                    data-testid="submit-event-button"
                  >
                    Create Event
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {events.length === 0 ? (
          <Card className="p-8 text-center bg-slate-900/50 border border-white/5 rounded-xl" data-testid="no-events-message">
            <p className="text-slate-400">No events scheduled yet. Check back soon!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {events.map((event) => {
              const isSignedUp = event.signups?.includes(user.id);
              const isFull = event.max_attendees && event.signups?.length >= event.max_attendees;
              
              return (
                <Card
                  key={event.id}
                  className="p-6 bg-slate-900/50 border border-white/5 rounded-xl hover:border-purple-500/30 transition-all"
                  data-testid={`event-card-${event.id}`}
                >
                  <h3 className="text-2xl font-bold text-white mb-3" data-testid={`event-title-${event.id}`}>{event.title}</h3>
                  <p className="text-slate-300 mb-4" data-testid={`event-description-${event.id}`}>{event.description}</p>
                  
                  <div className="space-y-2 mb-4 text-slate-400">
                    <div className="flex items-center gap-2">
                      <CalendarIcon className="w-4 h-4 text-purple-400" />
                      <span data-testid={`event-date-${event.id}`}>{new Date(event.date).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-purple-400" />
                      <span data-testid={`event-location-${event.id}`}>{event.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-400" />
                      <span data-testid={`event-attendees-${event.id}`}>
                        {event.signups?.length || 0}
                        {event.max_attendees ? ` / ${event.max_attendees}` : ""} attendees
                      </span>
                    </div>
                  </div>

                  <Button
                    onClick={() => signupForEvent(event.id)}
                    disabled={isSignedUp || isFull}
                    className={`w-full font-semibold ${
                      isSignedUp 
                        ? "bg-emerald-500/20 text-emerald-400" 
                        : isFull 
                        ? "bg-slate-700 text-slate-400" 
                        : "bg-yellow-500/100 hover:bg-yellow-400 text-black"
                    }`}
                    data-testid={`signup-event-button-${event.id}`}
                  >
                    {isSignedUp ? "✓ Registered" : isFull ? "Event Full" : "Sign Up"}
                  </Button>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}